// Publish (prototype publish dialog): what this version changes, an optional
// note, and the server's refusal reasons when it cannot be published (an
// error in the settings, an unchecked function). Unsaved edits are saved
// first, so what is published is what is on screen.

import { useEffect, useState } from 'react'
import { Check, Info } from 'lucide-react'
import { tw } from '../../testHooks'
import { KitModal } from '../../designerKit/KitModal'
import { useSetup } from './state'
import { Banner, HintBox } from './ui'

function failure(err: unknown): { status: number | null; message: string; errors: string[] } {
  const e = err as { status?: number; detail?: { message?: string; details?: { errors?: string[] } }; message?: string; response?: { status?: number; data?: { detail?: { message?: string; details?: { errors?: string[] } } | string } } }
  const status = e?.status ?? e?.response?.status ?? null
  const d = e?.detail ?? (typeof e?.response?.data?.detail === 'object' ? e.response.data.detail : undefined)
  const message = d?.message || (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : '') || e?.message || 'Publishing failed'
  return { status, message, errors: d?.details?.errors || [] }
}

export default function PublishDialog({ onClose }: { onClose: () => void }) {
  const { draft, transport, objName, notify, forms } = useSetup()
  const [phase, setPhase] = useState<'saving' | 'ready' | 'publishing'>('saving')
  const [changes, setChanges] = useState<string[]>(draft.changes)
  const [summary, setSummary] = useState('')
  const [refused, setRefused] = useState<{ message: string; errors: string[] } | null>(null)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const saved = await draft.flush()
      if (cancelled) return
      if (!saved) { setRefused({ message: 'Your last change was not saved, so there is nothing new to publish yet. Fix or discard it first.', errors: [] }); setPhase('ready'); return }
      try {
        const g = await transport.get(objName)
        if (!cancelled) setChanges(g.changes || [])
      } catch (err) {
        console.warn('[uda setup] the change list could not be read', err)
      }
      if (!cancelled) setPhase('ready')
    })()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async () => {
    setPhase('publishing')
    setRefused(null)
    try {
      const r = await transport.publish(objName, draft.version, summary.trim() || undefined)
      await draft.reload()
      notify('success', `Published version ${r.version}.`)
      onClose()
    } catch (err) {
      const f = failure(err)
      console.warn('[uda setup] publish refused', err)
      if (f.status === 409) await draft.reload()
      setRefused({ message: f.status === 409 ? 'Someone else changed these settings in the meantime. The newest settings are shown; check them and publish again.' : f.message, errors: f.errors })
      setPhase('ready')
    }
  }
  return (
    <KitModal open onClose={onClose} title={phase === 'saving' ? 'Publish' : `Publish ${changes.length} change${changes.length === 1 ? '' : 's'}?`}
      footer={(
        <>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Not yet</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={phase !== 'ready' || !changes.length}
            onClick={() => { void publish() }} {...tw('action', 'uda-publish-confirm')}>
            {phase === 'publishing' ? 'Publishing…' : 'Publish'}
          </button>
        </>
      )}>
      <div className="uds-dlg" style={{ display: 'grid', gap: 10 }} {...tw('screen', 'uda-publish')}>
        {phase === 'saving' && <div className="uds-hint"><span className="uds-spin" /> Saving your last changes…</div>}
        {refused && (
          <Banner tone="e">
            <b>{refused.message}</b>
            {!!refused.errors.length && <ul>{refused.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
          </Banner>
        )}
        {phase !== 'saving' && !changes.length && !refused && <div className="uds-na">Nothing has changed since the last version.</div>}
        <div>
          {changes.map((t, i) => <div key={i} className="uds-chg"><Check size={15} /><span>{t}</span></div>)}
        </div>
        <label className="uds-lbl" htmlFor="uds-psum">Note for this version (optional)</label>
        <input id="uds-psum" className="uds-inp" value={summary} onChange={e => setSummary(e.target.value)}
          placeholder="What changed and why" />
        <HintBox icon={<Info size={15} />}>
          Users of your organization get these the next time they open {forms[0]?.title ? <b>{forms[0].title}</b> : 'this screen'}. Values already saved are kept.
        </HintBox>
      </div>
    </KitModal>
  )
}
